<script setup>
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import articleApi from '@/api/ArticleApi';
import boardApi from '@/api/BoardApi';
import { formatCustomDate, getBoardName } from '@/composables/functions';
import {
  canonicalUrl,
  buildArticleTitle,
  buildArticleDescription,
  buildArticleHeadMeta,
  hasInlineSchema,
} from '@/utils/seo';
import { BoardTitleBox } from '@/components/board';
import IconView from '@/components/svg/IconView.vue';
import { patchArticleLike } from '@/api/EowayApi';
import { saveComment, getComments, editComment, delComment } from '@/api/boardCommentApi.js';
import {
  branchShareDataMap,
  convertPathToBranchPage,
  createBranchLink,
} from '@/composables/useBranch';
import { DEV_MODE } from '@/config/dev-mode';
import { useDeviceDetect } from '@/composables/useDeviceDetect';
import { useSimpleLinkIntercept } from '@/composables/useInterceptInternalExternalLinks.js';
import BoardCommentList from './BoardCommentList.vue';
import BoardCommentDrawer from './BoardCommentDrawer.vue';
import CommentActionMenu from './CommentActionMenu.vue';
import CommentReportDrawer from './CommentReportDrawer.vue';
import ConfirmComponent from '@/components/common/Confirm.vue';
import PostSharePopup from '../nangman-center/PostSharePopup.vue';
import { useBackLayer } from '@/composables/useAppBack';

const { isMobile } = useDeviceDetect();

const props = defineProps({
  board: {
    type: String,
    default: '',
  },
  boardId: {
    type: Number,
    default: 0,
  },
  articleId: {
    type: String,
    default: '',
  },
});

const route = useRoute();
const router = useRouter();
const store = useStore();

const {
  VITE_KAKAO_CHANNEL_BASE_URL,
  VITE_KAKAO_CHANNEL_PUBLIC_ID,
  VITE_WEB_BASE_URL,
} = import.meta.env;

const ADD_KAKAO_CHANNEL_URL = `${VITE_KAKAO_CHANNEL_BASE_URL}/${VITE_KAKAO_CHANNEL_PUBLIC_ID}`;

const userId = store.getters['user/id'];

const listData = ref({});
const articleCtn = ref(null);
const likeData = ref({ likeCnt: null, likeClicked: false });
// 댓글 리스트 강제 리렌더용 키
const commentListKey = ref(0);
const isSecret = ref(false);

const isWebView = typeof window !== 'undefined' && !!window.ReactNativeWebView;

const boardTitle = ref(null);
const comment = ref(null);
// const memberInfo = ref(null)
const memberId = ref(null);
/** 댓글 드로어 */
const isBoardCommentOpen = ref(false);
const activeArticleId = ref(null);

console.log('board는 무엇인가요?', props.board);
const getBoardInfo = async () => {
  try {
    const result = await boardApi().findById(props.boardId);
    boardTitle.value = result.boardTtl;
  } catch (error) {
    alert(error.response.data?.message);
  }
};

const getView = async () => {
  try {
    const result = await articleApi().get(props.boardId, props.articleId, {});

    if (!result) {
      router.back();
      return;
    }

    listData.value = result?.data;
    const rawHtml = isWebView
      ? result.data.articleCtnM?.trim() || result.data.articleCtn || ''
      : result.data.articleCtn || '';
    // 운영자 메모용 HTML 주석은 화면/SEO에 노출되지 않도록 제거
    articleCtn.value = rawHtml.replace(/<!--[\s\S]*?-->/g, '');
    comment.value = listData.value.comments[0]?.curseMasking;
    // memberInfo.value = store.getters[""]
    memberId.value = store.getters['user/id'];

    likeData.value.likeCnt = listData.value.likeCnt;
    likeData.value.likeClicked = listData.value.likeChecked;
  } catch (error) {
    alert(error);
  }
};

const goViewList = () => {
  router.push({
    path: `/nangman-center/${props.board}`,
    query: {
      // 쿼리 파라미터 추가
      tabCate: tabCate.value,
    },
  });
};

// 게시글 상세 SEO 메타 (가이드 v1.4 시트 3 자동 생성 규칙)
useHead(() => {
  const data = listData.value;
  if (!data?.articleTtl) return {};

  const boardName = getBoardName(props.board, 'name') || '낭만센터';
  const articleTitle = data.articleTtl;
  const fullTitle = buildArticleTitle(articleTitle, boardName);
  const description = buildArticleDescription({
    subtitle: data.articleTtlSub,
    html: articleCtn.value,
  });
  const image = data.thumbnail?.fileUri || undefined;
  const url = canonicalUrl(route.fullPath);

  const isEvent = props.board === 'event';
  const jsonLd = isEvent
    ? {
        '@context': 'https://schema.org',
        '@type': 'Event',
        name: articleTitle,
        description,
        url,
        startDate: data.eventStartDt || data.regDt,
        endDate: data.eventEndDt || data.eventStartDt || data.regDt,
        eventStatus: 'https://schema.org/EventScheduled',
        eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
        organizer: {
          '@type': 'Organization',
          name: '낭만만세력',
          url: 'https://nangmancalendar.com',
        },
      }
    : {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: articleTitle,
        description,
        url,
        datePublished: data.regDt,
        dateModified: data.updDt || data.regDt,
        author: { '@type': 'Organization', name: '낭만만세력' },
        publisher: { '@type': 'Organization', name: '낭만만세력' },
      };

  // 본문에 Schema.org 마크업이 인라인으로 들어있으면 자동 JSON-LD 생성 스킵
  // (운영자가 본문에 직접 작성한 풍부한 Schema가 있는 경우 중복 충돌 방지)
  const inlineSchemaPresent = hasInlineSchema(articleCtn.value);

  return buildArticleHeadMeta({
    fullTitle,
    description,
    image,
    url,
    publishedAt: data.regDt,
    modifiedAt: data.updDt,
    jsonLd: inlineSchemaPresent ? undefined : jsonLd,
  });
});

const del = async () => {
  if (confirm('삭제하시겠습니까?')) {
    try {
      const result = await articleApi().delete(props.boardId, props.articleId, {});
      if (result) {
        goViewList();
      }
    } catch (error) {
      alert(error.response.data?.message);
    }
  }
};

const tabCate = ref(null);

onMounted(async () => {
  getView();
  getBoardInfo();
  nextTick(() => autoResize());
  fetchComments(props.articleId);
  tabCate.value = route.query.cate;

  /** Branch SDK 로드 */
  const script = document.createElement('script');
  script.src = 'https://cdn.branch.io/branch-latest.min.js';
  script.async = true;
  document.head.appendChild(script);
  /** Branch - 스크립트 로드 완료 시 필요한 설정 */
  const options = { open_app: true };
  script.onload = () => {
    branch.init(import.meta.env.VITE_BRANCH_KEY, options, () => {});
  };

  // 댓글 영역으로 스크롤 처리
  if (route.query.scrollToComment === 'true') {
    await nextTick();
    setTimeout(() => {
      const commentBox = document.querySelector('.articel-comment-box');
      if (commentBox) {
        commentBox.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }, 500); // 페이지 로딩 완료 후 스크롤
  }
});

onUnmounted(() => {
  /** Branch - 언마운트 시 SDK 스크립트 제거 (선택 사항) */
  const script = document.querySelector('script[src="https://cdn.branch.io/branch-latest.min.js"]');
  if (script) {
    document.head.removeChild(script);
  }
});

const noDate = computed(() => ['word', 'faq'].includes(props.board));

/** 카카오 채널 추가하기 */
const addKakaoChannel = () => {
  window.open(ADD_KAKAO_CHANNEL_URL, '_blank');
};

/** 좋아요 버튼 클릭 */
const patchLike = async () => {
  try {
    const result = await patchArticleLike(props.articleId);
    likeData.value = result;
  } catch (error) {}
};

// ========================= 공유하기 =========================
const showPostSharePopup = ref(false);
const closePopSharePopup = () => (showPostSharePopup.value = false);

// ---- 모바일 환경 감지 ----
const ua = navigator.userAgent || '';
const isRN = typeof window.ReactNativeWebView !== 'undefined';
const isIOS = /iPad|iPhone|iPod/i.test(ua);
const isAndroid = /Android/i.test(ua);
const isAndroidWebView = isRN && isAndroid;

/** 공유하기 데이터 */
const shareItem = ref(null);

const sendShareMessage = async () => {
  if (window.ReactNativeWebView) {
    const messageData = {
      action: 'share',
      payload: {
        shareData: await shareWithApp(shareItem.value),
      },
    };
    window.ReactNativeWebView.postMessage(JSON.stringify(messageData));
  }
};

/** 공유 취소 에러 판별 */
const isShareCanceledError = (error) => {
  const errorName = error?.name || '';
  const errorMessage = error?.message || '';
  return (
    errorName === 'AbortError' ||
    /abort|cancel|dismiss/i.test(errorName) ||
    /abort|cancel|dismiss/i.test(errorMessage)
  );
};

/** 기기 기본 공유 (navigator.share) */
const shareByNative = async (item) => {
  if (typeof navigator === 'undefined' || typeof navigator.share !== 'function')
    return false;

  const baseUrl = DEV_MODE ? 'https://d25web.thegam.io' : VITE_WEB_BASE_URL;
  const webUrl = `${baseUrl}${route.path}`;

  const shareData = {
    title: item.articleTtl || '낭만센터',
    text: branchShareDataMap[convertPathToBranchPage(route.path)]?.messagePreset
      || branchShareDataMap['default'].messagePreset,
    url: webUrl,
  };

  if (typeof navigator.canShare === 'function' && !navigator.canShare(shareData))
    return false;

  await navigator.share(shareData);
  return true;
};

/** 공유하기 버튼 클릭 */
const onPressShare = async (item) => {
  if (!item) return null;

  shareItem.value = item;

  // RN WebView 환경
  if (window.ReactNativeWebView) {
    if (isAndroidWebView) {
      sendShareMessage();
    } else {
      showPostSharePopup.value = true;
    }
    return;
  }

  // 모바일 웹: navigator.share 우선 시도
  const isMobileDevice = window.innerWidth <= 768;
  if (isMobileDevice) {
    try {
      const isShared = await shareByNative(item);
      if (isShared) return;
    } catch (error) {
      if (isShareCanceledError(error)) return;
      DEV_MODE && console.error('기기 기본 공유 실패:', error);
    }
  }

  // 데스크톱 또는 navigator.share 실패 시 카카오 공유
  shareWithKakao(item);
};

const clickShareType = async ({ type }) => {
  if (window.ReactNativeWebView) {
    if (type === 'kakao') shareWithKakao(shareItem.value);
    if (type === 'link') sendShareMessage();
  }
};

const shareWithApp = async (item) => {
  // 앱에 전달할 페이지
  const page = convertPathToBranchPage(route.path);

  const messagePreset =
    page && branchShareDataMap[page]
      ? branchShareDataMap[page].messagePreset
      : branchShareDataMap['default'].messagePreset;

  // 카톡 공유하기 이미지
  const thumbnailUrl = `${VITE_WEB_BASE_URL}/secure/feedThumbnail.png`;
  const imageUrl = item?.thumbnail?.fileUri || thumbnailUrl;

  const branchLinkPayload = {
    page,
    contentId: item.articleId,
    contentTitle: item.articleTtl,
    imageUrl,
    routePath: route.path,
    login: userId,
  };

  let branchUrl = '';
  branchUrl = await createBranchLink(branchLinkPayload);
  return { branchUrl, branchLinkPayload, messagePreset };
};

/** 카카오 공유하기 */
const shareWithKakao = async (item) => {
  // 앱에 전달할 페이지
  const page = convertPathToBranchPage(route.path);

  // 카톡 공유하기 메시지
  const message =
    page && branchShareDataMap[page]
      ? branchShareDataMap[page].messagePreset
      : branchShareDataMap['default'].messagePreset;

  // 카톡 공유하기 이미지
  const thumbnailUrl = `${VITE_WEB_BASE_URL}/secure/feedThumbnail.png`;
  const imageUrl = item?.thumbnail?.fileUri || thumbnailUrl;

  const branchLinkPayload = {
    page,
    contentId: item.articleId,
    contentTitle: item.articleTtl,
    imageUrl,
    routePath: route.path,
    login: userId,
  };

  let branchUrl = '';
  try {
    branchUrl = await createBranchLink(branchLinkPayload);
  } catch (e) {
    DEV_MODE && console.log('branch link 생성에 실패하였습니다.');
  }

  const baseUrl = DEV_MODE ? 'https://d25web.thegam.io' : VITE_WEB_BASE_URL;
  const webUrl = `${baseUrl}${route.path}`;

  if (typeof Kakao !== 'undefined') {
    if (!Kakao.isInitialized()) {
      await Kakao.init(import.meta.env.VITE_KAKAO_APP_KEY);
    }
  } else {
    DEV_MODE && console.error('Kakao SDK가 로드되지 않았습니다.');
    return;
  }

  Kakao.Share.sendDefault({
    objectType: 'feed',
    content: {
      title: item.articleTtl,
      description: message,
      imageUrl,
      link: {
        webUrl,
        mobileWebUrl: branchUrl,
        androidExecutionParams: `screen=${page}&articleId=${item.articleId}`,
        iosExecutionParams: `screen=${page}&articleId=${item.articleId}`,
      },
    },
    buttons: [
      {
        title: '자세히 보기',
        link: {
          webUrl,
          mobileWebUrl: branchUrl,
          androidExecutionParams: `screen=${page}&articleId=${item.articleId}`,
          iosExecutionParams: `screen=${page}&articleId=${item.articleId}`,
        },
      },
    ],
  });
};

/** 파일 다운 시 원래 이름으로 다운되도록 */
const downloadFile = async (url, filename) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  } catch (error) {
    console.error('File download failed:', error);
  }
};

const articleBox = ref(null);
watch(articleCtn, async () => {
  await nextTick();
  const links = articleBox.value?.querySelectorAll('a') || [];
  links.forEach((link) => {
    // target 속성 제거
    link.removeAttribute('target');
    link.setAttribute('rel', 'noopener');
  });
});

/** html속 url 가로채기 */
useSimpleLinkIntercept(articleBox, {
  devMode: DEV_MODE,
  // 필요 시 내부 호스트를 직접 지정:
  // internalHosts: DEV_MODE
  //   ? ['d25web.thegam.io']
  //   : ['nangmancalendar.com', 'www.nangmancalendar.com'],
  debug: false,
});

/** 모바일 댓글창 열기 */
function openReplyDrawer() {
  activeArticleId.value = props.articleId;
  isBoardCommentOpen.value = true;
  // toggleBodyScroll(true)
}
/** body 스크롤 제어 */
function toggleBodyScroll(disable) {
  if (disable) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
  }
}
function closeReplyDrawer() {
  isBoardCommentOpen.value = false;
  activeArticleId.value = null;
  // toggleBodyScroll(false) // body 스크롤 복원
}

/** 댓글 PC ver */
// TODO: 컴포저블로 분리하여 모바일과 PC 공통으로 사용하도록 (CommentDrawer내부 script가 최신버전)
const actionOpen = ref(false);
const targetId = ref(null);
const targetComment = ref(null);
/** 대댓글 시 원댓글 작성자 이름 */
const replyToAuthorName = ref('');
/** 신고 드로어 */
const isReportOpen = ref(false);
const dialogTitle = ref('');
const dialogTitle2 = ref('');
const dialogText = ref('');
const dialogNextUrl = ref('');
const dialogConfirm = ref(null);
const warningTxt = ref('');
const kebabType = ref('');
const currentUserId = computed(() => sessionStorage.getItem('authUserId') ?? '');
const params = {
  InquiryStatus: '',
  systemId: 5,
  name: '',
  upperCommentId: null,
  upperWriterUserId: null,
  commentType: 'ARTICLE',
  writerUserId: null,
  files: [],
};
function buildPayload() {
  return {
    ...params,
    name: commentText.value,
    writerUserId: currentUserId.value,
    isPrivate: isSecret.value,
  };
}
const hasText = computed(() => commentText.value.trim().length > 0);
const comments = ref(null);
const editingId = ref(null); // 수정 댓글 ID저장용
const isEditing = computed(() => editingId.value != null);
const submitLabel = computed(() => (isEditing.value ? '수정' : '등록'));
const isReplying = ref(false);
const upperComment = ref(null);
// 현재 페이지 추적
const currentPage = ref(1);

let inFlight = false;
async function fetchComments(articleId, page = 1) {
  if (!articleId || inFlight) return;
  inFlight = true;
  // loading.value = true
  // error.value = null
  try {
    let targetPage = page;
    if (page === 'last') {
      // 댓글 수를 먼저 가져와서 마지막 페이지 계산
      const { getCommentCount } = await import('@/api/boardCommentApi');
      const cnt = await getCommentCount(articleId, 'ARTICLE');
      const totalCount = Number(cnt) || 0;
      const PAGE_SIZE = 10;
      targetPage = totalCount > 0 ? Math.ceil(totalCount / PAGE_SIZE) : 1;
    }
    console.log('fetchComments try');
    const res = await getComments(props.articleId, 'ARTICLE', { Page: targetPage });
    comments.value = res;
    // 현재 페이지 업데이트
    currentPage.value = targetPage;
    console.log('', comments.value);
  } catch (e) {
    // error.value = e
  } finally {
    // loading.value = false
    inFlight = false;
  }
}
// 댓글 입력창
const commentText = ref('');
const taRef = ref(null);
function autoResize(e) {
  const el = e?.target || taRef.value;
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = el.scrollHeight + 'px';
}
function onKeydown(e) {
  // Enter키로 등록 비활성화 - 등록 버튼으로만 등록
  // if (e.key === 'Enter' && !e.shiftKey) {
  //   e.preventDefault()
  //   submit()
  // }
}
async function submit() {
  if (!hasText.value) return;
  const payload = buildPayload();

  try {
    if (isEditing.value) {
      await editComment(props.articleId, { ...payload, commentId: editingId.value });
      // 댓글 수정 시에는 현재 페이지 유지
      await fetchComments(props.articleId, currentPage.value);
      // currentPage는 이미 올바른 값으로 설정됨
    } else if (isReplying.value) {
      await saveComment(props.articleId, { ...payload, upperCommentId: upperComment.value });
      // 댓글 등록 후 마지막 페이지로 이동
      await fetchComments(props.articleId, 'last');
      // fetchComments에서 currentPage가 마지막 페이지로 업데이트됨
    } else {
      await saveComment(props.articleId, payload);
      // 댓글 등록 후 마지막 페이지로 이동
      await fetchComments(props.articleId, 'last');
      // fetchComments에서 currentPage가 마지막 페이지로 업데이트됨
    }
    commentText.value = '';
    editingId.value = null;
    upperComment.value = null;
    isEditing.value = false;
    isReplying.value = false;
    await nextTick();
    autoResize();
    commentListKey.value++;
  } catch (err) {
    DEV_MODE && console.error(isEditing.value ? 'editComment 실패' : 'saveComment 실패', err);
  }
}
// 1,000자 제한
const MAX_LEN = 1000;
const charCount = computed(() => commentText.value.length);

/** 옵션 메뉴 */
// TODO: action과 report를 하나의 메서드로 리팩토링
function openActions(comment) {
  targetComment.value = comment;
  targetId.value = comment.commentId;
  actionOpen.value = true;
  kebabType.value = 'action';
}
function openReport(comment) {
  targetComment.value = comment;
  targetId.value = comment.commentId;
  actionOpen.value = true;
  kebabType.value = 'report';
}
function closeMenu() {
  actionOpen.value = false;
  kebabType.value = '';
}
/** 댓글 수정 */
function onEdit(id) {
  targetId.value = id;
  editingId.value = id;

  const list = comments.value || [];
  const target = list.find((c) => String(c.commentId) === String(id));
  commentText.value = target?.name ?? '';

  // 수정할 댓글의 upperCommentId 정보 저장
  if (target?.upperCommentId) {
    params.upperCommentId = target.upperCommentId;
  } else {
    params.upperCommentId = null;
  }

  nextTick(() => {
    autoResize();
    taRef.value?.focus();
    const len = commentText.value.length;
    try {
      taRef.value?.setSelectionRange(len, len);
    } catch (_) {}
  });
}
/** 댓글 삭제 */
async function onDelete(id) {
  showDialogConfirm('정말 삭제하시겠습니까?', '삭제 후 복원이 불가능합니다.', '확인', () =>
    confirmDelete(id),
  );
}
/** 삭제 확인 */
const confirmDelete = async (id) => {
  try {
    const res = await delComment(id);
    if (res) {
      // 댓글 삭제 후 마지막 페이지로 이동
      await fetchComments(props.articleId, 'last');
      commentListKey.value++;
    } else {
      alert('댓글 삭제 실패'); // 임시 조치
    }
  } catch (err) {
    DEV_MODE && console.error('게시판 댓글 삭제 실패', err);
  }
};
const showDialogConfirm = (title, title2 = '', text = '', onConfirm = () => {}, warning) => {
  dialogTitle.value = title;
  dialogTitle2.value = title2;
  dialogText.value = text;
  dialogNextUrl.value = onConfirm;
  warningTxt.value = warning;
  dialogConfirm.value.openConfirmDialog();
};
/** 페이지 변경 핸들러 */
const handlePageChange = (p) => {
  currentPage.value = p;
  fetchComments(props.articleId, p);
};

/** 대댓글 */
const onNestedReplyClick = async (comment) => {
  if (!currentUserId.value) return;

  replyToAuthorName.value = comment.nickname ?? comment.userName ?? '낭만회원';
  isReplying.value = true;
  upperComment.value = comment.commentId;

  // 스크롤을 페이지 하단으로 이동
  nextTick(() => {
    window.scrollTo({
      top: document.body.scrollHeight,
      behavior: 'smooth',
    });
  });
};
/** 신고하기 드로어 오픈 */
const onReport = (comment) => {
  isReportOpen.value = true;
  targetComment.value = comment;
};
const closeReportDrawer = () => {
  isReportOpen.value = false;
};

useBackLayer({
  id: 'board-share',
  isOpen: showPostSharePopup,
  closeFromBack: closePopSharePopup,
});
useBackLayer({
  id: 'board-comment-actions',
  isOpen: actionOpen,
  closeFromBack: closeMenu,
});
useBackLayer({
  id: 'board-comment-report',
  isOpen: isReportOpen,
  closeFromBack: closeReportDrawer,
});
// ========================= 모바일 앱과 통신 =========================
/** 모바일 하단 여백 */
const insetBottom = ref(20);

onMounted(async () => {
  window.addEventListener('message', handleMessage);
  document.addEventListener('message', handleMessage);
});

onUnmounted(() => {
  window.removeEventListener('message', handleMessage);
  document.removeEventListener('message', handleMessage);
});

/** 모바일 앱과 통신 */
const handleMessage = async (event) => {
  try {
    let data;

    if (typeof event.data === 'string') {
      data = JSON.parse(event.data);
    } else {
      data = event.data;
    }

    if (data.source === 'react-devtools-content-script') return;
    if (data.key === '__VUE_DEVTOOLS_VUE_DETECTED_EVENT__') return;

    // 인증 상태는 글로벌 RN 메시지 핸들러에서만 관리한다.
    if (data.action === 'init') {
      if (data?.payload?.bottom) insetBottom.value = data?.payload?.bottom;
    }
  } catch (e) {
    console.error('Invalid message received:', `${e}`);
  }
};
</script>

<template>
  <div class="" :style="isMobile ? { paddingBottom: `${insetBottom}px` } : {}">
    <div
      :class="{
        'mobile-board__sticky-top': isMobile,
        'wrap-as-none': !isMobile,
      }"
    >
      <BoardTitleBox :board="board" :show-back="true" />
      <div v-if="!isMobile" class="board-view-tit-st2-box">
        <div class="like-box">
          <button class="icon-box" @click="patchLike()">
            <IconLike class="icon-like" :class="{ active: likeData.likeClicked }" />
          </button>
          <button class="icon-share none-txt w24" @click="onPressShare(listData)">공유하기</button>
        </div>
        <div class="tit-box">
          <div class="tit-b">
            <h1 class="article-title">{{ listData.articleTtl }}</h1>
            <div class="art-icon-box">
              <span
                v-for="(icon, index2) in listData.icon ? listData.icon.split(',') : []"
                :key="index2"
                class="txt"
                :class="icon"
              >
                {{ icon === 'RECO' ? '추천' : icon }}
              </span>
            </div>
          </div>
          <div class="tit-s">
            {{ listData.articleTtlSub }}
          </div>
        </div>
        <div v-if="!noDate" class="date-box">
          <time
            v-if="props.board === 'event'"
            class="date"
            :datetime="listData.eventStartDt"
          >
            {{ formatCustomDate(listData.eventStartDt) }} ~
            {{ formatCustomDate(listData.eventEndDt) }}
          </time>
          <time v-else class="date" :datetime="listData.displayDate">
            {{ formatCustomDate(listData.displayDate) }}
          </time>
          <div class="hit">
            <span class="icon-hit"><IconView /></span><span>{{ listData.viewCount }}</span>
          </div>
        </div>
      </div>
      <div v-if="isMobile" class="board-view-tit-st2-box">
        <div class="like-box">
          <button class="icon-box" @click="patchLike()">
            <IconLike class="icon-like" :class="{ active: likeData.likeClicked }" />
          </button>
          <button class="icon-share none-txt w24" @click="onPressShare(listData)">공유하기</button>
        </div>
        <div class="mobile-board-view__tit">
          <div v-if="!noDate" class="date-box">
            <time
              v-if="props.board === 'event'"
              class="date"
              :datetime="listData.eventStartDt"
            >
              {{ formatCustomDate(listData.eventStartDt) }} ~
              {{ formatCustomDate(listData.eventEndDt) }}
            </time>
            <time v-else class="date" :datetime="listData.displayDate">
              {{ formatCustomDate(listData.displayDate) }}
            </time>
            <div class="hit">
              <span class="icon-hit"><IconView /></span><span>{{ listData.viewCount }}</span>
            </div>
          </div>
          <div class="tit-box">
            <div class="tit-b">
              <h1 class="article-title">{{ listData.articleTtl }}</h1>
            </div>
            <div class="tit-s">
              {{ listData.articleTtlSub }}
            </div>
          </div>
          <div class="art-icon-box">
            <span
              v-for="(icon, index2) in listData.icon ? listData.icon.split(',') : []"
              :key="index2"
              class="txt"
              :class="icon"
            >
              {{ icon === 'RECO' ? '추천' : icon }}
            </span>
          </div>
        </div>
      </div>
    </div>
    <article class="article-content-box">
      <div ref="articleBox" class="" v-html="articleCtn" />
    </article>
    <div class="board-del-btn-box">
      <button v-if="memberId === listData.userId" class="btn-del-line-3" @click="del()">
        삭제
      </button>
    </div>
    <div v-if="comment" class="board-answer-box">
      <div class="answer-tit">만세력 운영진 답변</div>
      <div class="answer-cont">
        <div class="" v-html="comment" />
      </div>
    </div>
    <div class="article-file-box" v-if="listData.files && listData.files.length > 0">
      <div v-if="!isMobile" class="tit-s">첨부파일</div>
      <div class="file-list">
        <div
          v-for="file in listData.files"
          :key="file.id"
          class="item-list"
          @click="downloadFile(file.fileUri, file.fileNameClient)"
        >
          <span>{{ file.fileNameClient }}</span>
        </div>
      </div>
    </div>

    <div class="article-keyword-box" v-if="listData.keywords && listData.keywords.length > 0">
      <span v-for="(keyword, index) in listData.keywords" :key="index" class="key-text">
        #{{ keyword }}
      </span>
    </div>
    <!-- TODO: 댓글기능 DEV_MODE 제한 제거 -->
    <div
      v-if="
        DEV_MODE && props.board !== 'notice' && props.board !== 'essay' && props.board !== 'faq'
      "
      class="articel-comment-box"
    >
      <template v-if="isMobile && listData.hasComment === true">
        <div class="reaction-box">
          <button class="reply-btn" @click.stop="openReplyDrawer(item)"></button>
          <span class="reaction-count"></span>
        </div>
      </template>
      <template v-else>
        <BoardCommentList
          :key="commentListKey"
          :comments="comments"
          :currentUserId="currentUserId"
          :articleId="articleId"
          :currentPage="currentPage"
          :articleHasComment="listData.hasComment"
          @openActions="openActions"
          @openReport="openReport"
          @onNestedReplyClick="onNestedReplyClick"
          @page-changed="handlePageChange"
        />
        <div
          v-if="currentUserId && listData.hasComment === true"
          class="article-comment__comment-input"
        >
          <!-- 대댓글 시 원댓글 작성자 이름 -->
          <div v-if="isReplying" class="board-comment__replying-notice">
            <span
              ><span class="author-name">@ {{ replyToAuthorName }}</span> 님에게 답글 남기는
              중</span
            >
            <div class="comment-drawer__close" @click="isReplying = false"></div>
          </div>
          <div class="board-comment__write-area">
            <textarea
              :id="`comment-${articleId ?? 'new'}`"
              ref="taRef"
              v-model="commentText"
              class="comment-input-plain"
              placeholder="댓글을 입력하세요"
              rows="1"
              @input="autoResize"
              @keydown="onKeydown"
              autocapitalize="sentences"
              autocomplete="off"
              autocorrect="off"
              :maxlength="MAX_LEN"
            />
            <div class="board-comment__comment-counter">{{ charCount }} / 1,000</div>
          </div>
          <div class="board-comment__bar">
            <button type="button" class="board-comment__secret-btn" @click="isSecret = !isSecret">
              <div class="secret-comment-icon" :class="{ active: isSecret }" />
              비밀댓글
            </button>
            <button
              class="board-comment__add-btn"
              :class="{ active: hasText }"
              :disabled="!hasText"
              @click="submit"
            >
              {{ submitLabel }}
            </button>
          </div>
        </div>
      </template>
    </div>
    <div v-if="!isMobile" class="line-wd100"></div>
    <section class="btn-com gap-xl center" :class="{ 'app-board__go-list-btn': isMobile }">
      <button class="btn-line w-large bg-w" @click="goViewList()">목록으로</button>
    </section>
  </div>

  <!-- TODO: 댓글기능 DEV_MODE 제한 제거 -->
  <BoardCommentDrawer
    v-if="DEV_MODE"
    v-model="isBoardCommentOpen"
    :article-id="activeArticleId"
    :articleHasComment="listData.hasComment"
    @close="closeReplyDrawer"
  />
  <!-- 액션 / 신고 / 확인 레이어 (리스트에서도 동작하도록 상시 렌더) -->
  <CommentActionMenu
    v-model="actionOpen"
    :target-id="targetId"
    :type="kebabType"
    @edit="onEdit"
    @delete="onDelete"
    @report="onReport"
    @cancel="closeMenu"
  />

  <CommentReportDrawer
    v-model="isReportOpen"
    :targetComment="targetComment"
    @close="closeReportDrawer"
  />

  <ConfirmComponent
    ref="dialogConfirm"
    :title="dialogTitle"
    :title2="dialogTitle2"
    :text="dialogText"
    :nextUrl="dialogNextUrl"
    :warning="warningTxt"
  />

  <PostSharePopup v-if="showPostSharePopup" @close="closePopSharePopup" @share="clickShareType" />
</template>

<style scoped>
/* 시맨틱 마크업: h1 기본 스타일을 부모(.tit-b)의 폰트 설정 그대로 따르도록 reset */
.article-title {
  margin: 0;
  font: inherit;
  color: inherit;
}
</style>
